import type { Controls } from "@obean/game-contract";

/** Button bits shared by every input device (games decide what they mean; tank: 0 = fire). */
export const Button = { primary: 1, secondary: 2, ability: 4 } as const;

export const NO_CONTROLS: Controls = { moveX: 0, moveY: 0, buttons: 0 };

/** Keyboard layout: WASD or arrows to move; Space/J fire, K secondary, L ability. */
const KEYS: Record<string, { x?: number; y?: number; button?: number }> = {
  KeyW: { y: -1 },
  ArrowUp: { y: -1 },
  KeyS: { y: 1 },
  ArrowDown: { y: 1 },
  KeyA: { x: -1 },
  ArrowLeft: { x: -1 },
  KeyD: { x: 1 },
  ArrowRight: { x: 1 },
  Space: { button: Button.primary },
  KeyJ: { button: Button.primary },
  KeyK: { button: Button.secondary },
  KeyL: { button: Button.ability },
};

export const isGameKey = (code: string) => code in KEYS;

/**
 * Controls from the set of held keys (KeyboardEvent.code). The most recently pressed direction wins
 * on each axis, so rolling from one key to another never stalls.
 */
export function controlsFromKeys(held: readonly string[]): Controls {
  let moveX = 0;
  let moveY = 0;
  let buttons = 0;
  for (const code of held) {
    const key = KEYS[code];
    if (!key) continue;
    if (key.x !== undefined) moveX = key.x;
    if (key.y !== undefined) moveY = key.y;
    if (key.button !== undefined) buttons |= key.button;
  }
  const length = Math.hypot(moveX, moveY);
  return length > 1
    ? { moveX: moveX / length, moveY: moveY / length, buttons }
    : { moveX, moveY, buttons };
}

/**
 * A virtual stick: where the thumb is relative to where it landed, as a vector of length ≤ 1
 * (full deflection at `radius` CSS pixels). Screen axes: x right, y down.
 */
export function stickVector(
  origin: { x: number; y: number },
  thumb: { x: number; y: number },
  radius: number,
): { moveX: number; moveY: number } {
  const dx = thumb.x - origin.x;
  const dy = thumb.y - origin.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return { moveX: 0, moveY: 0 };
  const scale = Math.min(length, radius) / length / radius;
  return { moveX: dx * scale, moveY: dy * scale };
}

/** Several devices at once (a laptop with a touch screen): the strongest stick wins, buttons add up. */
export function mergeControls(sources: readonly Controls[]): Controls {
  let best = NO_CONTROLS;
  let buttons = 0;
  for (const c of sources) {
    if (Math.hypot(c.moveX, c.moveY) > Math.hypot(best.moveX, best.moveY)) best = c;
    buttons |= c.buttons;
  }
  return { moveX: best.moveX, moveY: best.moveY, buttons };
}

/** A game's declared buttons (manifest.controls, ADR 0017). */
export type ControlButtons = readonly {
  button: "primary" | "secondary" | "ability";
  label: string;
}[];

/** The keyboard key the platform gives each button (KEYS above). */
const KEY_NAME = { primary: "Space", secondary: "K", ability: "L" } as const;

/** The keyboard hint for a game: moving, then each button it declares with its key. */
export function keyboardHint(buttons: ControlButtons): string {
  return ["Move: WASD/arrows", ...buttons.map((b) => `${b.label}: ${KEY_NAME[b.button]}`)].join(
    " · ",
  );
}
